// API client for backend communication
// Replaces direct Supabase client usage

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

class ApiClient {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/api`;
    this.token = null;
    this.tokenRefresher = null;
    this.onAuthFailure = null;
    this.teamId = null;
  }

  setToken(token) {
    if (!token) {
      console.warn('⚠️ Attempting to set empty token');
      return;
    }
    this.token = token;
  }

  // Set the current team ID (called from TeamContext)
  setTeamId(teamId) {
    this.teamId = teamId;
  }

  // Get the current team ID
  getTeamId() {
    return this.teamId;
  }

  // Set the token refresh function (called from Auth0DataContext)
  setTokenRefresher(refreshFn) {
    this.tokenRefresher = refreshFn;
  }

  // Set callback for auth failures that can't be recovered
  setAuthFailureHandler(handler) {
    this.onAuthFailure = handler;
  }

  async request(endpoint, options = {}, isRetry = false) {
    // Add team ID as query parameter if set (avoids CORS preflight issues with custom headers)
    let url = `${this.baseUrl}${endpoint}`;
    if (this.teamId) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}teamId=${this.teamId}`;
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else {
      console.warn('⚠️ No token available for request:', endpoint);
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 Unauthorized - attempt token refresh and retry once
      if (response.status === 401 && !isRetry && this.tokenRefresher) {
        console.log('🔄 Token expired, attempting refresh...');
        try {
          // Pass true to force a new token (bypass cache) since we got a 401
          const newToken = await this.tokenRefresher(true);
          if (newToken) {
            this.setToken(newToken);
            console.log('✅ Token refreshed, retrying request...');
            return this.request(endpoint, options, true);
          }
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError);
          if (this.onAuthFailure) {
            this.onAuthFailure();
          }
          throw new Error('Session expired. Please log in again.');
        }
      }

      // Handle 401 on retry or without refresher - auth failure
      if (response.status === 401) {
        console.error('❌ Authentication failed');
        if (this.onAuthFailure) {
          this.onAuthFailure();
        }
        throw new Error('Session expired. Please log in again.');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      // Handle empty responses
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // ==================== TEAMS ====================

  async getTeams() {
    return this.request('/teams');
  }

  async createTeam(data) {
    return this.request('/teams', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTeam(teamId) {
    return this.request(`/teams/${teamId}`);
  }

  async updateTeam(teamId, data) {
    return this.request(`/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTeam(teamId) {
    return this.request(`/teams/${teamId}`, {
      method: 'DELETE',
    });
  }

  async getTeamMembers(teamId) {
    return this.request(`/teams/${teamId}/members`);
  }

  async addTeamMember(teamId, userId, role = 'member') {
    return this.request(`/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    });
  }

  async updateTeamMember(teamId, memberId, role) {
    return this.request(`/teams/${teamId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async removeTeamMember(teamId, memberId) {
    return this.request(`/teams/${teamId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async leaveTeam(teamId) {
    return this.request(`/teams/${teamId}/leave`, {
      method: 'POST',
    });
  }

  async transferOwnership(teamId, newOwnerId) {
    return this.request(`/teams/${teamId}/transfer-ownership`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerId }),
    });
  }

  async switchTeam(teamId) {
    return this.request(`/teams/switch/${teamId}`, {
      method: 'POST',
    });
  }

  // ==================== USER MANAGEMENT ====================

  async ensureUserProfile(userId, email, name) {
    return this.request('/users/ensure-profile', {
      method: 'POST',
      body: JSON.stringify({ userId, email, name }),
    });
  }

  async getAllProfiles() {
    return this.request('/profiles');
  }

  async updateProfile(id, data) {
    return this.request(`/profiles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProfile(id) {
    return this.request(`/profiles/${id}`, {
      method: 'DELETE',
    });
  }

  // Lightweight user list for pickers (no role data, any authed user can call)
  async getAllUsers() {
    return this.request('/users');
  }

  // ==================== AUTH0 USER MANAGEMENT ====================

  async getAllAuth0Users() {
    return this.request('/auth0/users');
  }

  async getAuth0User(id) {
    return this.request(`/auth0/users/${id}`);
  }

  async createAuth0User(data) {
    return this.request('/auth0/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAuth0User(id, data) {
    return this.request(`/auth0/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAuth0User(id) {
    return this.request(`/auth0/users/${id}`, {
      method: 'DELETE',
    });
  }

  async resetUserPassword(id) {
    return this.request(`/auth0/users/${id}/reset-password`, {
      method: 'POST',
    });
  }

  // ==================== AUTH0 ROLE MANAGEMENT ====================

  async getAllAuth0Roles() {
    return this.request('/auth0/roles');
  }

  async getRole(id) {
    return this.request(`/auth0/roles/${id}`);
  }

  async createRole(data) {
    return this.request('/auth0/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(id, data) {
    return this.request(`/auth0/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(id) {
    return this.request(`/auth0/roles/${id}`, {
      method: 'DELETE',
    });
  }

  async getUserRoles(userId) {
    return this.request(`/auth0/users/${userId}/roles`);
  }

  async assignRolesToUser(userId, roleIds) {
    return this.request(`/auth0/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ roleIds }),
    });
  }

  async removeRolesFromUser(userId, roleIds) {
    return this.request(`/auth0/users/${userId}/roles`, {
      method: 'DELETE',
      body: JSON.stringify({ roleIds }),
    });
  }

  async getUsersInRole(roleId) {
    return this.request(`/auth0/roles/${roleId}/users`);
  }

  // ==================== REVENUE & GROSS MARGIN ====================

  async getRevenueGm(year = null) {
    const endpoint = year ? `/revenue-gm?year=${year}` : '/revenue-gm';
    return this.request(endpoint);
  }

  async getRevenueGmYears() {
    return this.request('/revenue-gm/years');
  }

  async initializeRevenueGmYear(year) {
    return this.request('/revenue-gm/initialize-year', {
      method: 'POST',
      body: JSON.stringify({ year }),
    });
  }

  async updateRevenue(data) {
    return this.request('/revenue-gm/revenue', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGrossMargin(data) {
    return this.request('/revenue-gm/gross-margin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== HISTORICAL DATA ====================

  async getHistoricalRevenueGm() {
    return this.request('/historical-revenue-gm');
  }

  async replaceHistoricalData(data) {
    console.log('📡 API Client: replaceHistoricalData called with', data.length, 'records');
    console.log('📡 API Client: First record:', data[0]);
    console.log('📡 API Client: Request URL:', `${this.baseUrl}/historical-revenue-gm/replace`);
    console.log('📡 API Client: Has token:', !!this.token);

    try {
      const result = await this.request('/historical-revenue-gm/replace', {
        method: 'POST',
        body: JSON.stringify({ data }),
      });
      console.log('📡 API Client: replaceHistoricalData response:', result);
      return result;
    } catch (error) {
      console.error('📡 API Client: replaceHistoricalData FAILED:', error);
      throw error;
    }
  }

  // ==================== BUSINESS LINES ====================

  async getBusinessLines(year = null) {
    const endpoint = year ? `/business-lines?year=${year}` : '/business-lines';
    return this.request(endpoint);
  }

  async getBusinessLinesYears() {
    return this.request('/business-lines/years');
  }

  async initializeBusinessLinesYear(year) {
    return this.request('/business-lines/initialize-year', {
      method: 'POST',
      body: JSON.stringify({ year }),
    });
  }

  async createBusinessLine(data) {
    return this.request('/business-lines', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBusinessLine(id, data) {
    return this.request(`/business-lines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteBusinessLine(id) {
    return this.request(`/business-lines/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== SALES TEAM ====================

  async getSalesTeam(year = null) {
    const endpoint = year ? `/sales-team?year=${year}` : '/sales-team';
    return this.request(endpoint);
  }

  async getSalesTeamYears() {
    return this.request('/sales-team/years');
  }

  async initializeSalesTeamYear(year) {
    return this.request('/sales-team/initialize-year', {
      method: 'POST',
      body: JSON.stringify({ year }),
    });
  }

  async createSalesTeamMember(data) {
    return this.request('/sales-team', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSalesTeamMember(id, data) {
    return this.request(`/sales-team/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSalesTeamMember(id) {
    return this.request(`/sales-team/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== MEETINGS ====================

  async getMeetings() {
    return this.request('/meetings');
  }

  async createMeeting(data) {
    return this.request('/meetings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMeeting(id, data) {
    return this.request(`/meetings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMeeting(id) {
    return this.request(`/meetings/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== OFFERS ====================

  async getOffers(year = null) {
    const endpoint = year ? `/offers?year=${year}` : '/offers';
    return this.request(endpoint);
  }

  async getOffersYears() {
    return this.request('/offers/years');
  }

  async initializeOffersYear(year) {
    return this.request('/offers/initialize-year', {
      method: 'POST',
      body: JSON.stringify({ year }),
    });
  }

  async createOffer(data) {
    return this.request('/offers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOffer(id, data) {
    return this.request(`/offers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteOffer(id) {
    return this.request(`/offers/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== PROJECTS ====================

  async getProjects() {
    return this.request('/projects');
  }

  async createProject(data) {
    return this.request('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(id, data) {
    return this.request(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id) {
    return this.request(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== PROJECT COLUMNS ====================

  async getProjectColumns() {
    return this.request('/project-columns');
  }

  async createProjectColumn(data) {
    return this.request('/project-columns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProjectColumn(id, data) {
    return this.request(`/project-columns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProjectColumn(id) {
    return this.request(`/project-columns/${id}`, {
      method: 'DELETE',
    });
  }

  async replaceProjectColumns(columns) {
    return this.request('/project-columns/replace', {
      method: 'POST',
      body: JSON.stringify({ columns }),
    });
  }

  // ==================== CRM (pipeline) ====================

  async getCrmAccounts(params = {}) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/crm/accounts${qs ? `?${qs}` : ''}`);
  }

  async getCrmAccount(id) {
    return this.request(`/crm/accounts/${id}`);
  }

  async createCrmAccount(data) {
    return this.request('/crm/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCrmAccount(id, data) {
    return this.request(`/crm/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async claimCrmAccount(id, userId) {
    return this.request(`/crm/accounts/${id}/claim`, {
      method: 'POST',
      body: JSON.stringify(userId ? { userId } : {}),
    });
  }

  async releaseCrmAccount(id) {
    return this.request(`/crm/accounts/${id}/release`, {
      method: 'POST',
    });
  }

  async deleteCrmAccount(id) {
    return this.request(`/crm/accounts/${id}`, {
      method: 'DELETE',
    });
  }

  async addCrmNote(id, data) {
    return this.request(`/crm/accounts/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== CHAT SETTINGS ====================

  async getChatSettings() {
    return this.request('/chat-settings');
  }

  async updateChatSettings(data) {
    return this.request('/chat-settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== KNOWLEDGE BASE ====================

  async getKnowledgeBase() {
    return this.request('/knowledge-base');
  }

  async createKnowledgeBaseDocument(data) {
    return this.request('/knowledge-base', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteKnowledgeBaseDocument(id) {
    return this.request(`/knowledge-base/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== CHAT MESSAGES ====================

  async getChatMessages() {
    return this.request('/chat-messages');
  }

  async createChatMessage(data) {
    return this.request('/chat-messages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAllChatMessages() {
    return this.request('/chat-messages', {
      method: 'DELETE',
    });
  }

  // ==================== ADMIN (User Management) ====================

  async getAllTeamsAdmin() {
    return this.request('/admin/all-teams');
  }

  async adminDeleteTeam(teamId, force = false) {
    return this.request(`/admin/teams/${teamId}${force ? '?force=true' : ''}`, {
      method: 'DELETE',
    });
  }

  async getUserTeams(userId) {
    return this.request(`/admin/user-teams/${encodeURIComponent(userId)}`);
  }

  // ==================== STREAMING CHAT (Claude) ====================

  /**
   * Get chat configuration from server
   */
  async getChatConfig() {
    return this.request('/chat/config');
  }

  /**
   * Get chat history from server (legacy - all conversations)
   */
  async getChatHistory(limit = 50) {
    return this.request(`/chat/history?limit=${limit}`);
  }

  /**
   * Clear all chat history on server
   */
  async clearChatHistory() {
    return this.request('/chat/history', {
      method: 'DELETE',
    });
  }

  // ==================== CONVERSATIONS ====================

  /**
   * Get all conversations for current user
   */
  async getConversations() {
    return this.request('/chat/conversations');
  }

  /**
   * Create a new conversation
   */
  async createConversation(title) {
    return this.request('/chat/conversations', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  /**
   * Update a conversation (e.g., rename)
   */
  async updateConversation(conversationId, title) {
    return this.request(`/chat/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    });
  }

  /**
   * Delete a conversation and all its messages
   */
  async deleteConversation(conversationId) {
    return this.request(`/chat/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get messages for a specific conversation
   */
  async getConversationMessages(conversationId) {
    return this.request(`/chat/conversations/${conversationId}/messages`);
  }

  /**
   * Stream a chat message (returns EventSource-like interface)
   * @param {string} message - User message
   * @param {Array} history - Previous messages
   * @param {Object} options - Additional options
   * @param {string} options.conversationId - Existing conversation ID (or null for new)
   * @param {Function} options.onContent - Callback for text content
   * @param {Function} options.onToolUse - Callback for tool use events
   * @param {Function} options.onToolResult - Callback for tool results
   * @param {Function} options.onDone - Callback when complete (receives messageId, conversationId)
   * @param {Function} options.onConversationCreated - Callback when new conversation is created
   * @param {Function} options.onError - Callback for errors
   * @param {number} options.messageHistoryLimit - Max messages to include
   */
  async streamChat(message, history = [], options = {}) {
    const {
      conversationId,
      onContent,
      onToolUse,
      onToolResult,
      onDone,
      onConversationCreated,
      onError,
      messageHistoryLimit,
    } = options;

    const url = `${this.baseUrl}/chat/stream`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          message,
          conversationId,
          history,
          messageHistoryLimit,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      // Process the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'content':
                  if (onContent) onContent(data.text);
                  break;
                case 'tool_use':
                  if (onToolUse) onToolUse(data.name, data.input);
                  break;
                case 'tool_result':
                  if (onToolResult) onToolResult(data.name, data.success, data.error);
                  break;
                case 'conversation_created':
                  if (onConversationCreated) onConversationCreated(data.conversationId, data.title);
                  break;
                case 'done':
                  if (onDone) onDone(data.messageId, data.conversationId);
                  break;
                case 'error':
                  if (onError) onError(new Error(data.message));
                  break;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line);
            }
          }
        }
      }

    } catch (error) {
      console.error('Stream chat error:', error);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
